/* MARK:- adorn-builder: <adorn-builder> visual bundle builder. See docs/adorn-visual-builder-spec.md.
 *
 * Verification TODO (spec §13) — blocking gates deferred per explicit decision to build the
 * system layer before dev-store access. Confirm all of these on a live FoxSell-enabled store
 * before shipping to production:
 *   V1 Property-survival spike — do __foxsell:* + the hidden bead-order property survive the
 *      Cart Transform Function to cart/checkout/order/email? If stripped, the ordering design in
 *      #buildCartPayload below must move to cart attributes or FoxSell-side notes.
 *   V2 Duplicate-entry semantics — repeated bead colors submitted as repeated bundleItems entries;
 *      confirm FoxSell doesn't silently collapse them in a way that loses placement order.
 *   V3 Pricing composition — post-transform cart total must equal #getPrice() = Σ selected
 *      component variant prices (verified 2026-10-14: 8400 + 400 → 8800).
 *   V4 Sold-out component — confirm FoxSell's behavior when an unavailable component is submitted.
 *   V5 Min/max enforcement — confirm graceful server-side outcome on a devtools bypass attempt.
 *   V6 Cart surfaces — notification, mini-cart, cart-page qty/remove all match existing behavior.
 *   V7 Template wiring — no JS errors from anything expecting #foxsell-bundles on this template.
 *   V8 Palette integrity — all components resolve; config payload weight is sane.
 */

(function () {
  const SVG_NS = 'http://www.w3.org/2000/svg';
  // MARK:- camera cap; keep bead geometry fixed while the viewport follows the run.
  const MAX_ZOOM = 2.5;
  const XLINK_NS = 'http://www.w3.org/1999/xlink';

  if (customElements.get('adorn-builder')) return;

  customElements.define('adorn-builder', class AdornBuilder extends HTMLElement {
    connectedCallback() {
      const configEl = this.querySelector('[data-adorn-builder-config]');
      if (!configEl) return;

      this.config = JSON.parse(configEl.textContent);
      this.state = { baseVariantId: null, beads: [], submitting: false };
      this._uidCounter = 0;

      this.#cacheDom();
      this.#buildLookups();
      this.#setupArcPath();
      this._beadDiameter = this.config.beadSize;
      this.#bindEvents();

      document.body.classList.add('js-page---has-product-builder');

      const firstAvailableBase = this.baseVariants.find((v) => v.available !== false) || this.baseVariants[0];
      if (firstAvailableBase) this.state.baseVariantId = firstAvailableBase.id;

      this.render();
    }

    // ---- DOM caching -------------------------------------------------

    #cacheDom() {
      this.baseEl = this.querySelector('[data-adorn-role="base"]');
      this.previewSvgEl = this.querySelector('[data-adorn-role="preview-svg"]');
      this.cameraEl = this.querySelector('[data-adorn-role="camera"]');
      this.arcPathEl = this.querySelector('[data-adorn-role="arc-path"]');
      this.baseImageEl = this.querySelector('[data-adorn-role="base-image"]');
      this.beadLayerEl = this.querySelector('[data-adorn-role="bead-layer"]');
      this.maxWarningEl = this.querySelector('[data-adorn-role="max-warning"]');
      this.paletteEl = this.querySelector('[data-adorn-role="palette"]');
      this.summaryListEl = this.querySelector('[data-adorn-role="summary-list"]');
      this.priceEl = this.querySelector('[data-adorn-role="price"]');
      this.clearButton = this.querySelector('[data-adorn-role="clear"]');
      this.atcButton = this.querySelector('[data-adorn-role="atc"]');
      this.announcerEl = this.querySelector('[data-adorn-role="announcer"]');
      this.confirmEl = this.querySelector('[data-adorn-role="confirm"]');
      this.confirmCancelBtn = this.querySelector('[data-adorn-role="confirm-cancel"]');
      this.confirmConfirmBtn = this.querySelector('[data-adorn-role="confirm-confirm"]');
    }

    #buildLookups() {
      this.baseVariants = [];
      (this.config.base?.products || []).forEach((product) => {
        (product.variants || []).forEach((variant) => {
          this.baseVariants.push({ ...variant, productId: product.id, productTitle: product.title });
        });
      });
      this.baseByVariant = new Map(this.baseVariants.map((v) => [v.id, v]));
      this.paletteByVariant = new Map((this.config.palette?.items || []).map((item) => [item.variantId, item]));
      this.priceMap = new Map();
      this.baseVariants.forEach((v) => this.priceMap.set(v.id, v.price));
      (this.config.palette?.items || []).forEach((item) => this.priceMap.set(item.variantId, item.price));
    }

    #setupArcPath() {
      this.arcPathEl.setAttribute('d', this.config.arcPath);
      this._pathLength = this.arcPathEl.getTotalLength();
    }

    // ---- derived state -------------------------------------------------

    getMax() {
      // MARK:- component limits override builder defaults; missing limits remain unlimited.
      return this.baseByVariant.get(this.state.baseVariantId)?.max ?? this.config.max ?? Infinity;
    }

    getMin() {
      return this.baseByVariant.get(this.state.baseVariantId)?.min ?? this.config.min ?? 1;
    }

    getPrice() {
      let total = 0;
      if (this.state.baseVariantId != null) total += this.priceMap.get(this.state.baseVariantId) || 0;
      this.state.beads.forEach((b) => { total += this.priceMap.get(b.variantId) || 0; });
      return total;
    }

    canAddToCart() {
      if (this.state.baseVariantId == null) return false;
      const count = this.state.beads.length;
      if (count < this.getMin() || count > this.getMax()) return false;
      return this.#allSelectedAvailable();
    }

    #allSelectedAvailable() {
      const baseInfo = this.baseByVariant.get(this.state.baseVariantId);
      if (baseInfo && baseInfo.available === false) return false;
      return this.state.beads.every((b) => {
        const item = this.paletteByVariant.get(b.variantId);
        return !item || item.available !== false;
      });
    }

    #optionLabelPlural() {
      return `${this.config.optionLabel}s`;
    }

    #paletteTitleFor(variantId) {
      return this.paletteByVariant.get(variantId)?.title || '';
    }

    // ---- actions -------------------------------------------------

    addBead(variantId) {
      if (this.state.beads.length >= this.getMax()) {
        this.render({ announce: { type: 'blocked-max' } });
        return;
      }
      const item = this.paletteByVariant.get(variantId);
      if (item && item.available === false) {
        this.render({ announce: { type: 'blocked-unavailable', variantId } });
        return;
      }
      this.state.beads.push({ uid: this._uidCounter++, variantId });
      this.render({ announce: { type: 'add', variantId } });
    }

    removeBeadAt(index) {
      if (index < 0 || index >= this.state.beads.length) return;
      const [removed] = this.state.beads.splice(index, 1);
      const focusTarget = this.state.beads.length
        ? { region: 'summary', index: Math.max(0, index - 1) }
        : { region: 'palette' };
      this.render({ announce: { type: 'remove', variantId: removed.variantId }, focusTarget });
    }

    moveBead(from, to) {
      if (to < 0 || to >= this.state.beads.length || from === to) return;
      const [bead] = this.state.beads.splice(from, 1);
      this.state.beads.splice(to, 0, bead);
      this.render({ announce: { type: 'move', variantId: bead.variantId, to }, focusTarget: { region: 'summary', uid: bead.uid } });
    }

    clearBeads() {
      this.state.beads = [];
      this.render({ announce: { type: 'clear' }, focusTarget: { region: 'palette' } });
    }

    setBase(variantId) {
      // Spec §5.2: never truncate on a base swap even if it drops beads over the new max —
      // #updateMaxWarning() surfaces the "remove N to fit" message instead.
      this.state.baseVariantId = variantId;
      this.render({ announce: { type: 'base', variantId } });
    }

    // ---- render orchestration -------------------------------------------------

    render(opts = {}) {
      this.#updateBaseChipSelection();
      this.#updateBasePreviewImage();
      this.#updateBeadLayer();
      this.#updateCamera();
      this.#updatePaletteAvailability();
      this.#renderSummaryList();
      this.#updatePrice();
      this.#updateActions();
      this.#updatePreviewAriaLabel();
      this.#updateMaxWarning();
      if (opts.announce) this.#announce(this.#buildAnnouncement(opts.announce));
      if (opts.focusTarget && !this._drag) this.#applyFocusTarget(opts.focusTarget);
      this.dispatchEvent(new CustomEvent('builder:change', {
        bubbles: true,
        detail: { baseVariantId: this.state.baseVariantId, beads: this.state.beads.map((b) => b.variantId), price: this.getPrice(), canAddToCart: this.canAddToCart() },
      }));
    }

    // MARK:- zoom to the run, then clamp its centre so the viewport stays within the frame.
    #updateCamera() {
      const n = Math.max(1, this.state.beads.length);
      const spacing = this._beadSpacing;
      const zoom = this.config.layout === 'arc' ? Math.max(1, Math.min(MAX_ZOOM, 720 / ((n - 1) * spacing + this._beadDiameter))) : 1;
      const centre = this.arcPathEl.getPointAtLength(Math.min(this._pathLength, this.config.layout === 'arc' ? this._pathLength / 2 : (n - 1) * spacing / 2));
      this.cameraEl.style.setProperty('--cam-x', `${Math.max(500 / zoom, Math.min(1000 - 500 / zoom, centre.x))}px`);
      this.cameraEl.style.setProperty('--cam-y', `${Math.max(this.config.frameHeight / (2 * zoom), Math.min(this.config.frameHeight - this.config.frameHeight / (2 * zoom), centre.y))}px`);
      this.cameraEl.style.setProperty('--cam-z', zoom);
    }

    #buildAnnouncement(announce) {
      const optionLabel = this.config.optionLabel;
      const total = this.state.beads.length;
      switch (announce.type) {
        case 'add': {
          const title = this.#paletteTitleFor(announce.variantId);
          const max = this.getMax();
          const totalDisplay = Number.isFinite(max) ? max : total;
          return `Added ${title}, position ${total} of ${totalDisplay}`;
        }
        case 'remove': {
          const title = this.#paletteTitleFor(announce.variantId);
          return `Removed ${title}, ${total} ${this.#optionLabelPlural()} remaining`;
        }
        case 'move': {
          const title = this.#paletteTitleFor(announce.variantId);
          return `Moved ${title} to position ${announce.to + 1} of ${total}`;
        }
        case 'clear':
          return `Cleared all ${this.#optionLabelPlural()}`;
        case 'base': {
          const info = this.baseByVariant.get(announce.variantId);
          return info ? `${info.title} selected` : '';
        }
        case 'blocked-max':
          return `Maximum ${this.getMax()} ${this.#optionLabelPlural()} reached`;
        case 'blocked-unavailable': {
          const title = this.#paletteTitleFor(announce.variantId);
          return `${title} is sold out`;
        }
        default:
          return '';
      }
    }

    #announce(message) {
      if (!message) return;
      this.announcerEl.textContent = '';
      requestAnimationFrame(() => { this.announcerEl.textContent = message; });
    }

    #applyFocusTarget(target) {
      queueMicrotask(() => {
        if (target.region === 'palette') {
          this.paletteEl.querySelector('[data-adorn-role="swatch"]')?.focus();
          return;
        }
        if (target.region === 'summary') {
          const rows = this.summaryListEl.querySelectorAll('[data-adorn-role="summary-row"]');
          let row = null;
          if (target.uid != null) row = this.summaryListEl.querySelector(`[data-uid="${target.uid}"]`);
          if (!row) row = rows[target.index] || rows[rows.length - 1];
          row?.querySelector('[data-adorn-role="row-remove"]')?.focus();
        }
      });
    }

    // ---- base picker -------------------------------------------------

    #baseChipVariant(btn) {
      const current = this.baseByVariant.get(this.state.baseVariantId);
      const productId = btn.closest('fieldset').dataset.productId;
      if (!productId) {
        const variants = this.baseVariants.filter((v) => v.productId === Number(btn.dataset.value) && v.available !== false);
        return variants.find((v) => v.options.every((value, i) => value === current?.options[i])) || variants[0];
      }
      return this.baseVariants.find((v) => v.productId === Number(productId) && v.available !== false &&
        v.options.every((value, i) => value === (i === Number(btn.dataset.optionIndex) ? btn.dataset.value : current?.options[i])));
    }

    #updateBaseChipSelection() {
      const current = this.baseByVariant.get(this.state.baseVariantId);
      this.baseEl.querySelectorAll('fieldset').forEach((group) => {
        group.hidden = !!group.dataset.productId && Number(group.dataset.productId) !== current?.productId;
        const value = group.querySelector('[data-adorn-role="option-value"]');
        if (value) value.textContent = current?.options[Number(group.dataset.optionIndex)] || '';
      });
      this.baseEl.querySelectorAll('[data-adorn-role="base-chip"]').forEach((btn) => {
        const selected = btn.dataset.optionIndex == null
          ? Number(btn.dataset.value) === current?.productId
          : btn.dataset.value === current?.options[Number(btn.dataset.optionIndex)];
        btn.checked = selected;
        btn.disabled = !this.#baseChipVariant(btn);
        btn.setAttribute('aria-disabled', String(btn.disabled));
        btn.nextElementSibling.setAttribute('aria-disabled', String(btn.disabled));
      });
    }

    #updateBasePreviewImage() {
      const info = this.baseByVariant.get(this.state.baseVariantId);
      // MARK:- each base photo supplies its calibrated path.
      const arcPath = info?.arcPath || this.config.arcPath;
      if (this.arcPathEl.getAttribute('d') !== arcPath) {
        this.arcPathEl.setAttribute('d', arcPath);
        this._pathLength = this.arcPathEl.getTotalLength();
      }
      // MARK:- fit to this base's maximum so adding beads never changes their scale.
      const fit = Number.isFinite(this.getMax())
        ? Math.min(1, this._pathLength / (this.getMax() * this.config.beadSpacing))
        : 1;
      this._beadDiameter = this.config.beadSize * fit;
      this._beadSpacing = this.config.beadSpacing * fit;
      const href = info ? info.image : '';
      this.baseImageEl.classList.toggle('adorn-builder__base-image--hidden', !href);
      if (href) {
        this.baseImageEl.setAttributeNS(XLINK_NS, 'href', href);
        this.baseImageEl.setAttribute('href', href);
        this.baseImageEl.setAttribute('x', '0');
        this.baseImageEl.setAttribute('y', '0');
        this.baseImageEl.setAttribute('width', '1000');
        this.baseImageEl.setAttribute('height', this.config.frameHeight);
      }
    }

    // ---- palette -------------------------------------------------

    #updatePaletteAvailability() {
      const max = this.getMax();
      const atMax = this.state.beads.length >= max;
      this.paletteEl.querySelectorAll('[data-adorn-role="swatch"]').forEach((btn) => {
        const variantId = Number(btn.dataset.variantId);
        const item = this.paletteByVariant.get(variantId);
        const available = item ? item.available !== false : true;
        let blocked = false;
        let reason = '';
        if (!available) {
          blocked = true;
          reason = ', sold out';
        } else if (atMax) {
          blocked = true;
          reason = `, maximum ${max} reached`;
        }
        btn.setAttribute('aria-disabled', blocked ? 'true' : 'false');
        btn.setAttribute('aria-label', `Add ${item ? item.title : ''} ${this.config.optionLabel}${reason}`);
      });
    }

    // ---- preview (SVG) -------------------------------------------------

    #createBeadGroup(bead) {
      const g = document.createElementNS(SVG_NS, 'g');
      g.classList.add('adorn-builder__bead-slot');
      g.dataset.uid = bead.uid;
      g.setAttribute('aria-hidden', 'true');
      const hit = document.createElementNS(SVG_NS, 'ellipse');
      hit.classList.add('adorn-builder__bead-hit');
      hit.setAttribute('rx', (this.config.layout === 'arc' ? this._beadSpacing : this._beadDiameter) / 2);
      hit.setAttribute('ry', this._beadDiameter / 2);
      g.appendChild(hit);
      const fallback = document.createElementNS(SVG_NS, 'circle');
      fallback.dataset.adornRole = 'bead-fallback';
      g.appendChild(fallback);
      const image = document.createElementNS(SVG_NS, 'image');
      image.dataset.adornRole = 'bead-image';
      g.appendChild(image);
      const badge = document.createElementNS(SVG_NS, 'text');
      badge.classList.add('adorn-builder__bead-badge', 'h3');
      badge.setAttribute('x', this._beadDiameter / 3);
      badge.setAttribute('y', -this._beadDiameter / 3);
      badge.textContent = '×';
      g.appendChild(badge);
      return g;
    }

    #paintBeadImage(g, bead) {
      const item = this.paletteByVariant.get(bead.variantId);
      const href = item?.render || '';
      const r = this._beadDiameter / 2;
      const image = g.querySelector('[data-adorn-role="bead-image"]');
      image.setAttribute('x', -r);
      image.setAttribute('y', -r);
      image.setAttribute('width', this._beadDiameter);
      image.setAttribute('height', this._beadDiameter);
      image.setAttributeNS(XLINK_NS, 'href', href);
      image.setAttribute('href', href);
      image.setAttribute('visibility', href ? 'visible' : 'hidden');
      const fallback = g.querySelector('[data-adorn-role="bead-fallback"]');
      fallback.setAttribute('r', r);
      fallback.setAttribute('fill', item?.swatch || 'currentColor');
      fallback.setAttribute('opacity', item?.swatch ? '1' : '0.25');
      fallback.setAttribute('visibility', href ? 'hidden' : 'visible');
    }

    // MARK:- keyed path placement shared by necklaces and bottom-to-top threaders.
    #updateBeadLayer() {
      const beads = this.state.beads;
      const n = beads.length;
      const isStack = this.config.layout === 'stack';
      const slotCount = isStack && Number.isFinite(this.getMax()) ? Math.max(n, this.getMax()) : n;
      const startOffset = isStack ? 0 : (this._pathLength - Math.max(0, n - 1) * this._beadSpacing) / 2;
      const existing = new Map();
      this.beadLayerEl.querySelectorAll('[data-uid], [data-placeholder]').forEach((el) => {
        existing.set(el.dataset.uid ?? `empty-${el.dataset.placeholder}`, el);
      });
      const seen = new Set();

      for (let i = 0; i < slotCount; i++) {
        const bead = beads[i];
        const key = bead ? String(bead.uid) : `empty-${i}`;
        seen.add(key);
        let g = existing.get(key);
        if (!g) {
          if (bead) {
            g = this.#createBeadGroup(bead);
          } else {
            g = document.createElementNS(SVG_NS, 'g');
            g.dataset.placeholder = i;
            g.classList.add('adorn-builder__bead-slot', 'adorn-builder__bead-slot--empty');
            g.setAttribute('aria-hidden', 'true');
            const circle = document.createElementNS(SVG_NS, 'circle');
            circle.setAttribute('fill', 'none');
            circle.setAttribute('stroke', 'currentColor');
            g.appendChild(circle);
          }
          this.beadLayerEl.appendChild(g);
        }
        if (bead) {
          this.#paintBeadImage(g, bead);
          g.querySelector('.adorn-builder__bead-hit').setAttribute('rx', (isStack ? this._beadDiameter : this._beadSpacing) / 2);
          g.querySelector('.adorn-builder__bead-hit').setAttribute('ry', this._beadDiameter / 2);
          g.querySelector('.adorn-builder__bead-badge').setAttribute('x', this._beadDiameter / 3);
          g.querySelector('.adorn-builder__bead-badge').setAttribute('y', -this._beadDiameter / 3);
          g.setAttribute('aria-label', `Remove ${this.config.optionLabel} ${i + 1} of ${n}, ${this.#paletteTitleFor(bead.variantId)}`);
        } else {
          g.querySelector('circle').setAttribute('r', this._beadDiameter / 2);
        }
        const length = Math.max(0, Math.min(this._pathLength, startOffset + i * this._beadSpacing));
        const point = this.arcPathEl.getPointAtLength(length);
        const before = this.arcPathEl.getPointAtLength(Math.max(0, length - 1));
        const after = this.arcPathEl.getPointAtLength(Math.min(this._pathLength, length + 1));
        const angle = Math.atan2(after.y - before.y, after.x - before.x) * (180 / Math.PI) * (isStack ? 1 : 0.3);
        g.style.setProperty('--slot-x', `${point.x}px`);
        g.style.setProperty('--slot-y', `${point.y}px`);
        g.style.setProperty('--slot-rotate', `${angle}deg`);
      }
      existing.forEach((el, key) => { if (!seen.has(key)) el.remove(); });
    }

    #updatePreviewAriaLabel() {
      const titles = this.state.beads.map((b) => this.#paletteTitleFor(b.variantId));
      const max = this.getMax();
      const maxDisplay = Number.isFinite(max) ? ` of ${max}` : '';
      const description = titles.length
        ? `Preview: ${titles.join(', ')}, ${titles.length}${maxDisplay} ${this.#optionLabelPlural()}`
        : 'Preview: empty';
      this.previewSvgEl.setAttribute('aria-label', description);
    }

    #updateMaxWarning() {
      const max = this.getMax();
      const over = this.state.baseVariantId != null && this.state.beads.length > max;
      if (over) {
        const excess = this.state.beads.length - max;
        const noun = excess > 1 ? this.#optionLabelPlural() : this.config.optionLabel;
        this.maxWarningEl.textContent = `Remove ${excess} ${noun} to fit this ${this.config.baseLabel}`;
        this.maxWarningEl.classList.add('is-visible');
      } else if (Number.isFinite(max) && this.state.beads.length === max) {
        this.maxWarningEl.textContent = `Maximum ${max} ${max === 1 ? this.config.optionLabel : this.#optionLabelPlural()} reached`;
        this.maxWarningEl.classList.add('is-visible');
      } else {
        this.maxWarningEl.classList.remove('is-visible');
      }
    }

    // ---- summary list -------------------------------------------------

    #renderSummaryList() {
      const beads = this.state.beads;
      const total = beads.length;
      const [backLabel, forwardLabel] = this.config.layout === 'stack' ? ['down', 'up'] : ['left', 'right'];


      this.summaryListEl.innerHTML = '';
      this.querySelector('[data-adorn-role="empty-summary"]').hidden = !!total;

      beads.forEach((bead, i) => {
        const item = this.paletteByVariant.get(bead.variantId);
        const title = item ? item.title : '';

        const li = document.createElement('li');
        li.className = 'adorn-builder__summary-row';
        li.dataset.uid = bead.uid;
        li.dataset.adornRole = 'summary-row';

        const swatch = document.createElement('span');
        swatch.className = 'adorn-builder__row-swatch';
        if (item?.swatch) swatch.style.setProperty('--adorn-swatch-color', item.swatch);
        swatch.setAttribute('aria-hidden', 'true');
        if (item?.render) {
          const image = document.createElement('img');
          image.src = item.render;
          image.alt = '';
          image.width = 24;
          image.height = 24;
          swatch.appendChild(image);
        }
        li.appendChild(swatch);

        const label = document.createElement('span');
        label.className = 'adorn-builder__row-label';
        const position = document.createElement('span');
        position.className = 'adorn-builder__row-position';
        position.textContent = `${i + 1}. `;
        li.insertBefore(position, swatch);
        label.appendChild(document.createTextNode(title));
        li.appendChild(label);

        [
          ['row-move-back', backLabel, i === 0],
          ['row-move-forward', forwardLabel, i === total - 1],
        ].forEach(([role, directionLabel, disabled]) => {
          const moveButton = document.createElement('button');
          moveButton.type = 'button';
          moveButton.className = 'adorn-builder__row-move';
          moveButton.dataset.adornRole = role;
          moveButton.setAttribute('aria-label', `Move ${this.config.optionLabel} ${i + 1}, ${title}, ${directionLabel}`);
          moveButton.appendChild(this.querySelector('[data-adorn-role="arrow-icon"]').content.cloneNode(true));
          moveButton.classList.toggle('adorn-builder__row-move--stack', this.config.layout === 'stack');
          moveButton.disabled = disabled;
          li.appendChild(moveButton);
        });

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'adorn-builder__row-remove';
        remove.dataset.adornRole = 'row-remove';
        remove.setAttribute('aria-label', `Remove ${this.config.optionLabel} ${i + 1} of ${total}, ${title}`);
        remove.appendChild(this.querySelector('[data-adorn-role="close-icon"]').content.cloneNode(true));
        li.appendChild(remove);

        this.summaryListEl.appendChild(li);
      });
    }

    // ---- price / actions -------------------------------------------------

    #updatePrice() {
      this.priceEl.textContent = formatter.format(this.getPrice() / 100);
    }

    #updateActions() {
      this.atcButton.disabled = !this.canAddToCart() || this.state.submitting;
      this.atcButton.textContent = this.state.beads.length < this.getMin()
        ? `Add at least ${this.getMin()} ${this.getMin() === 1 ? this.config.optionLabel : this.#optionLabelPlural()}` : 'Add to bag';
      this.clearButton.classList.toggle('is-visible', !!this.state.beads.length);
    }

    // ---- clear-all confirm -------------------------------------------------

    #openConfirm() {
      this.confirmEl.hidden = false;
      trapFocus(this.confirmEl, this.confirmConfirmBtn);
    }

    #closeConfirm(restoreFocusToClear) {
      this.confirmEl.hidden = true;
      removeTrapFocus(restoreFocusToClear ? this.clearButton : null);
    }

    // ---- cart submission -------------------------------------------------

    #buildBundleItems() {
      // MARK:- FoxSell rejects items without type: 'product' ("Bundle selection not found", verified 2026-10-08);
      // string variant ids match product-bundler.js:380
      const items = [{
        variantId: String(this.state.baseVariantId),
        quantity: 1,
        category: this.config.base?.categoryTitle,
        type: 'product',
        properties: { _placement: this.config.base.categoryTitle.toLowerCase() },
      }];
      this.state.beads.forEach((bead, i) => {
        items.push({
          variantId: String(bead.variantId),
          quantity: 1,
          category: this.config.palette?.categoryTitle,
          type: 'product',
          properties: { _placement: `bead ${i + 1}` },
        });
      });
      return items;
    }

    #buildItemDetails(bundleItems) {
      return bundleItems.map((entry, i) => {
        if (i === 0) {
          const info = this.baseByVariant.get(Number(entry.variantId));
          return { title: info?.productTitle || '', variantTitle: info?.title || '' };
        }
        const item = this.paletteByVariant.get(Number(entry.variantId));
        return { title: `${i}. ${item?.title || ''}`, variantTitle: '' };
      });
    }

    #buildCartPayload(sectionsToRender) {
      const bundleItems = this.#buildBundleItems();
      const identifier = `${this.config.bundleId}_${Date.now()}`;
      const optionLabelPlural = this.#optionLabelPlural();
      const optionLabelPluralCap = optionLabelPlural.charAt(0).toUpperCase() + optionLabelPlural.slice(1);
      const visibleKey = `_${optionLabelPluralCap} (${this.config.directionLabel})`;
      const visibleValue = this.state.beads
        .map((b, i) => `${i + 1}. ${this.#paletteTitleFor(b.variantId)}`)
        .join(', ');

      return {
        items: [{
          id: this.config.parentVariantId,
          quantity: 1,
          properties: {
            '__foxsell:dynamic_add_on_bundle_id': identifier,
            '__foxsell:dynamic_add_on_bundle_items': JSON.stringify(bundleItems),
            '__bundle_item_details': JSON.stringify(this.#buildItemDetails(bundleItems)),
            '_custom_product': 'True',
            [visibleKey]: visibleValue,
            '_builder': JSON.stringify({
              v: 1,
              id: this.config.builderId,
              base: this.state.baseVariantId,
              beads: this.state.beads.map((b) => b.variantId),
            }),
          },
        }],
        sections: sectionsToRender.map((s) => s.section),
        sections_url: window.location.pathname,
      };
    }

    #handleAddToCart = async (event) => {
      event.preventDefault();
      if (this.state.submitting || !this.canAddToCart()) return;
      this.state.submitting = true;
      this.atcButton.disabled = true;
      const originalText = this.atcButton.textContent;
      this.atcButton.textContent = 'adding…';

      const sectionsToRender = this.#getSectionsToRender();

      try {
        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(this.#buildCartPayload(sectionsToRender)),
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.description || `Cart add failed: ${response.status}`);
        }
        const parsed = await response.json();
        this.#refreshCartUI(parsed, sectionsToRender);
        this.atcButton.textContent = 'added to bag';
        this.dispatchEvent(new CustomEvent('builder:added', { bubbles: true, detail: parsed }));
        setTimeout(() => { this.atcButton.textContent = originalText; }, 2000);
      } catch (error) {
        console.error('adorn-builder: add to cart failed', error);
        this.atcButton.textContent = originalText;
        this.#announce('Something went wrong adding to your bag. Please try again.');
      } finally {
        this.state.submitting = false;
        this.atcButton.disabled = !this.canAddToCart();
      }
    };

    #refreshCartUI(parsedState, sectionsToRender) {
      const notification = document.getElementById('cart-notification');
      if (notification) {
        notification.classList.add('active');
        notification.focus();
      }

      const miniCartSection = document.getElementById('shopify-section-mini-cart');
      const miniCartContainer = miniCartSection ? miniCartSection.querySelector('.mini-cart-container') : null;
      if (miniCartContainer) miniCartContainer.classList.add('not-empty');

      const mainCartItems = document.getElementById('main-cart-items');
      const mainCartFooter = document.getElementById('main-cart-footer');
      if (mainCartItems && mainCartFooter) {
        this.#updateCartSections(parsedState, sectionsToRender);
        // MARK:- reuse the shared free-shipping-bar/subtotal refresh other bundler flows call after
        // a cart-section swap (product-bundler.js:655, collection-bundler.js:461); guarded because
        // product-bundler.js isn't loaded on this template (spec §8) so the function may not exist
        if (typeof updateCartHeaderFooter === 'function') {
          updateCartHeaderFooter();
        }
      }
    }

    #getSectionsToRender() {
      const sections = [
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      ];
      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems) {
        sections.push({ id: 'main-cart-items', section: mainCartItems.dataset.id, selector: '.js-contents' });
      }
      const mainCartFooter = document.getElementById('main-cart-footer');
      if (mainCartFooter) {
        sections.push({ id: 'main-cart-footer', section: mainCartFooter.dataset.id, selector: '.js-contents' });
      }
      return sections;
    }

    #updateCartSections(parsedState, sectionsToRender) {
      sectionsToRender.forEach((section) => {
        const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector);
        if (!elementToReplace) return;
        const html = parsedState.sections[section.section];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const replacement = doc.querySelector(section.selector);
        if (replacement) elementToReplace.innerHTML = replacement.innerHTML;
      });
    }

    // ---- event binding (each listener attached exactly once) -------------------------------------------------

    #bindEvents() {
      this.atcButton.addEventListener('click', this.#handleAddToCart);
      this.clearButton.addEventListener('click', this.#handleClearClick);
      this.confirmCancelBtn.addEventListener('click', () => this.#closeConfirm(true));
      this.confirmConfirmBtn.addEventListener('click', () => {
        this.#closeConfirm(false);
        this.clearBeads();
      });
      this.confirmEl.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.#closeConfirm(true);
      });

      this.paletteEl.addEventListener('click', this.#handlePaletteClick);
      this.baseEl.addEventListener('change', this.#handleBaseClick);
      this.summaryListEl.addEventListener('click', this.#handleSummaryClick);
      this.beadLayerEl.addEventListener('click', this.#handlePreviewBeadClick);
      // MARK:- stable containers retain pointer capture while summary rows re-render.
      for (const surface of [this.beadLayerEl, this.summaryListEl]) {
        surface.addEventListener('pointerdown', this.#startDrag);
        surface.addEventListener('pointermove', this.#dragMove);
        surface.addEventListener('pointerup', this.#endDrag);
        surface.addEventListener('pointercancel', this.#endDrag);
        surface.addEventListener('lostpointercapture', this.#endDrag);
        surface.addEventListener('click', (event) => {
          if (!this._suppressDragClick) return;
          event.stopImmediatePropagation();
          event.preventDefault();
          this._suppressDragClick = false;
        }, true);
      }

    }

    // MARK:- pointer reorder: camera-local projection, coarse sampling then one refinement.
    #startDrag = (event) => {
      if (!event.isPrimary || event.button !== 0 || this._drag || event.target.closest('button')) return;
      const row = event.target.closest('[data-uid]');
      if (!row) return;
      this._suppressDragClick = false;
      this._drag = { uid: row.dataset.uid, pointerId: event.pointerId, surface: event.currentTarget,
        x: event.clientX, y: event.clientY, active: false };
      event.currentTarget.setPointerCapture(event.pointerId);
    };

    #dragMove = (event) => {
      const drag = this._drag;
      if (!drag || drag.pointerId !== event.pointerId) return;
      if (!drag.active && Math.hypot(event.clientX - drag.x, event.clientY - drag.y) < 6) return;
      drag.active = true;
      const from = this.state.beads.findIndex((bead) => String(bead.uid) === drag.uid);
      let to;
      const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(this.cameraEl.getScreenCTM().inverse());
      if (drag.surface === this.summaryListEl) {
        const rows = [...this.summaryListEl.children];
        const draggedRow = this.summaryListEl.querySelector(`[data-uid="${drag.uid}"]`);
        draggedRow.style.removeProperty('--drag-x');
        draggedRow.style.removeProperty('--drag-y');
        to = rows.reduce((best, row, index) => {
          const rect = row.getBoundingClientRect();
          const previous = rows[best].getBoundingClientRect();
          return Math.hypot(event.clientX - rect.x - rect.width / 2, event.clientY - rect.y - rect.height / 2)
            < Math.hypot(event.clientX - previous.x - previous.width / 2, event.clientY - previous.y - previous.height / 2) ? index : best;
        }, 0);
      } else {
        let length = 0;
        let distance = Infinity;
        const step = this._pathLength / 20;
        for (let pass = 0; pass < 2; pass++) {
          const start = pass ? Math.max(0, length - step) : 0;
          const end = pass ? Math.min(this._pathLength, length + step) : this._pathLength;
          for (let sample = 0; sample <= 20; sample++) {
            const offset = start + (end - start) * sample / 20;
            const candidate = this.arcPathEl.getPointAtLength(offset);
            const nextDistance = Math.hypot(point.x - candidate.x, point.y - candidate.y);
            if (nextDistance < distance) { distance = nextDistance; length = offset; }
          }
        }
        const startOffset = this.config.layout === 'stack' ? 0 : (this._pathLength - (this.state.beads.length - 1) * this._beadSpacing) / 2;
        to = Math.round((length - startOffset) / this._beadSpacing);
      }
      this.moveBead(from, Math.max(0, Math.min(this.state.beads.length - 1, to)));
      const slot = this.beadLayerEl.querySelector(`[data-uid="${drag.uid}"]`);
      slot.classList.add('is-dragging');
      if (drag.surface === this.beadLayerEl) {
        slot.style.setProperty('--slot-x', `${point.x}px`);
        slot.style.setProperty('--slot-y', `${point.y}px`);
      }
      const row = this.summaryListEl.querySelector(`[data-uid="${drag.uid}"]`);
      row.classList.add('is-dragging');
      if (drag.surface === this.summaryListEl) {
        const rect = row.getBoundingClientRect();
        row.style.setProperty('--drag-x', `${event.clientX - rect.x - rect.width / 2}px`);
        row.style.setProperty('--drag-y', `${event.clientY - rect.y - rect.height / 2}px`);
      }
    };

    #endDrag = (event) => {
      const drag = this._drag;
      if (!drag || drag.pointerId !== event.pointerId) return;
      this._drag = null;
      if (drag.surface.hasPointerCapture(event.pointerId)) drag.surface.releasePointerCapture(event.pointerId);
      if (!drag.active) {
        if (event.type === 'pointerup' && drag.surface === this.beadLayerEl) {
          this._suppressDragClick = true;
          this.removeBeadAt(this.state.beads.findIndex((bead) => String(bead.uid) === drag.uid));
        }
        return;
      }
      this._suppressDragClick = event.type === 'pointerup';
      this.beadLayerEl.querySelector(`[data-uid="${drag.uid}"]`).classList.remove('is-dragging');
      this.render();
    };

    #handleClearClick = () => {
      if (this.state.beads.length > 3) {
        this.#openConfirm();
      } else {
        this.clearBeads();
      }
    };

    #handlePaletteClick = (event) => {
      const btn = event.target.closest('[data-adorn-role="swatch"]');
      if (!btn) return;
      const beforeLength = this.state.beads.length;
      this.addBead(Number(btn.dataset.variantId));
      if (this.state.beads.length === beforeLength) {
        btn.classList.add('adorn-builder__swatch--shake');
        btn.addEventListener('animationend', () => btn.classList.remove('adorn-builder__swatch--shake'), { once: true });
      }
    };

    #handleBaseClick = (event) => {
      const btn = event.target.closest('[data-adorn-role="base-chip"]');
      if (!btn || btn.getAttribute('aria-disabled') === 'true') return;
      const variant = this.#baseChipVariant(btn);
      if (variant) this.setBase(variant.id);
    };

    #handleSummaryClick = (event) => {
      const row = event.target.closest('[data-adorn-role="summary-row"]');
      if (!row) return;
      const index = Array.from(this.summaryListEl.children).indexOf(row);
      if (event.target.closest('[data-adorn-role="row-remove"]')) {
        this.removeBeadAt(index);
      } else if (event.target.closest('[data-adorn-role="row-move-back"]')) {
        this.moveBead(index, index - 1);
      } else if (event.target.closest('[data-adorn-role="row-move-forward"]')) {
        this.moveBead(index, index + 1);
      }
    };

    #removeFromPreviewEvent(event) {
      const slot = event.target.closest('[data-uid]');
      if (!slot) return null;
      const uid = slot.dataset.uid;
      return this.state.beads.findIndex((b) => String(b.uid) === uid);
    }

    #handlePreviewBeadClick = (event) => {
      const index = this.#removeFromPreviewEvent(event);
      if (index != null && index > -1) this.removeBeadAt(index);
    };

  });
})();
